import { useUserGeneralProfile } from "@jugl-web/domain-resources/users/hooks/useUserGeneralProfile";
import { Avatar, InteractiveContainer } from "@jugl-web/ui-components";
import { cx, useAppVariant, useTranslations } from "@jugl-web/utils";
import { formatSecondsToDuration } from "@jugl-web/utils/date-time/formatSecondsToDuration";
import { FC } from "react";
import { ReactComponent as ChevronDownIcon } from "../../assets/chevron-down.svg";

interface HistoryHeaderProps {
  entityId: string;
  meId: string;
  totalDuration: number;
  /** Whether the history list is expanded (applies to the web only) */
  isExpanded?: boolean;
  /** Callback to toggle the expanded state (applies to the web only) */
  onToggleExpanded?: () => void;
}

export const HistoryHeader: FC<HistoryHeaderProps> = ({
  entityId,
  meId,
  totalDuration,
  isExpanded,
  onToggleExpanded,
}) => {
  const { safeProfile } = useUserGeneralProfile({ entityId, userId: meId });

  const { isMobile } = useAppVariant();

  const { t } = useTranslations();

  const innerContent = (
    <div className="flex h-10 items-center justify-between">
      <div className="flex items-center gap-2">
        <Avatar
          size="sm"
          username={safeProfile.displayName}
          imageUrl={safeProfile.avatar}
          className="shrink-0 border border-solid border-[#E0E0E0]"
        />
        <span className="text-dark font-secondary text-sm font-medium">
          {t({ id: "tasks-page.my-logs", defaultMessage: "My Logs" })}
        </span>
      </div>
      <div className="flex items-center gap-1">
        <span className="text-dark font-secondary text-sm font-medium tracking-[0.14px]">
          {formatSecondsToDuration(totalDuration)}
        </span>
        {!isMobile && (
          <ChevronDownIcon
            className={cx(
              "shrink-0 transition-transform",
              isExpanded && "rotate-180"
            )}
          />
        )}
      </div>
    </div>
  );

  if (isMobile) {
    return <div>{innerContent}</div>;
  }

  return (
    <InteractiveContainer
      className={cx(
        "hover:bg-grey-100 h-10 rounded-xl px-2 transition-colors",
        isExpanded && "bg-grey-100 border-b"
      )}
      onClick={onToggleExpanded}
    >
      {innerContent}
    </InteractiveContainer>
  );
};
